import { useCallback, useMemo, useState } from 'react';

import { MOBILE } from '@app/constants';
import { useWindowSize } from '@app/hooks';
import { getVisibleCalendarColumns } from '@app/pages/Calendar/utils';
import { useHorizontalDragScroll } from '@app/shared/hooks';
import type { HorizontalScrollModel } from '@app/shared/hooks';

import { useCalendarContext } from '@pages/Calendar/context';

import { useEmployeesViewportEffects } from './useEmployeesViewportEffects';

import type { EventDragState } from '../../AppointmentCard/hooks';
import type { RefCallback, WheelEvent } from 'react';

// A pointer press starting on an appointment card must move/resize it, never
// scroll the grid, so drag-to-scroll is suppressed on those targets.
const canStartGridDrag = (target: EventTarget | null): boolean =>
  !(target instanceof Element) || !target.closest('.AppointmentCard');

interface UseEmployeesViewportProps {
  totalEmployees: number;
}

export const useEmployeesViewport = ({
  totalEmployees,
}: UseEmployeesViewportProps) => {
  const { innerWidth } = useWindowSize();
  const { preferredMaxColumns } = useCalendarContext();
  const [viewportNode, setViewportNode] = useState<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  const [dragState, setDragState] = useState<EventDragState | null>(null);

  const dragStateChangeHandler = useCallback((state: EventDragState | null) => {
    setDragState(state);
  }, []);

  const visibleColumns = useMemo(
    () =>
      getVisibleCalendarColumns({
        availableGridWidth: viewportWidth || innerWidth,
        preferredMaxColumns,
        isMobile: innerWidth <= MOBILE,
      }),
    [innerWidth, preferredMaxColumns, viewportWidth]
  );

  const columnCount = useMemo(
    () => Math.min(totalEmployees, visibleColumns),
    [totalEmployees, visibleColumns]
  );

  const hasOverflow = useMemo(
    () => totalEmployees > visibleColumns,
    [totalEmployees, visibleColumns]
  );

  const columnWidth = useMemo(() => {
    if (!viewportWidth || !columnCount) {
      return 0;
    }

    const gap = columnCount > 1 ? (columnCount - 1) * 1 : 0;

    return (viewportWidth - gap) / columnCount;
  }, [viewportWidth, columnCount]);

  const totalTrackWidth = useMemo(() => {
    if (!columnWidth) {
      return 0;
    }

    const gap = totalEmployees > 1 ? (totalEmployees - 1) * 1 : 0;

    return totalEmployees * columnWidth + gap;
  }, [totalEmployees, columnWidth]);

  const maxScrollLeft = useMemo(
    () => Math.max(0, totalTrackWidth - viewportWidth),
    [totalTrackWidth, viewportWidth]
  );

  const setClampedScrollLeft = useCallback(
    (value: number) => {
      setScrollLeft(Math.min(Math.max(value, 0), maxScrollLeft));
    },
    [maxScrollLeft]
  );

  const scrollModel: HorizontalScrollModel = {
    contentWidth: totalTrackWidth,
    viewportWidth,
    scrollLeft,
    onScrollTo: setClampedScrollLeft,
  };

  // Touch/pointer drag-to-scroll on the grid. Wheel stays with the existing
  // `wheelHandler` (trackpad already works); the shared hook only adds drag,
  // which is the gap on touch screens. Merge its ref with the measuring node.
  const { ref: dragScrollRef } = useHorizontalDragScroll<HTMLDivElement>({
    model: scrollModel,
    withWheel: false,
    canStartDrag: canStartGridDrag,
  });

  const viewportRef = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      setViewportNode(node);
      dragScrollRef(node);
    },
    [dragScrollRef]
  );

  const isAtStart = useMemo(() => scrollLeft <= 0, [scrollLeft]);

  const isAtEnd = useMemo(
    () => scrollLeft >= maxScrollLeft - 1,
    [scrollLeft, maxScrollLeft]
  );

  const scrollByPage = useCallback(
    (direction: 'left' | 'right') => {
      const pageWidth = columnWidth * visibleColumns + (visibleColumns - 1) * 1;
      const delta = direction === 'left' ? -pageWidth : pageWidth;

      setClampedScrollLeft(scrollLeft + delta);
    },
    [columnWidth, scrollLeft, setClampedScrollLeft, visibleColumns]
  );

  const scrollToPrevPageHandler = useCallback(() => {
    scrollByPage('left');
  }, [scrollByPage]);

  const scrollToNextPageHandler = useCallback(() => {
    scrollByPage('right');
  }, [scrollByPage]);

  const wheelHandler = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      if (!hasOverflow) {
        return;
      }

      let delta = 0;

      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        delta = event.deltaX;
      } else if (event.shiftKey) {
        delta = event.deltaY;
      }

      if (delta === 0) {
        return;
      }

      event.preventDefault();
      setClampedScrollLeft(scrollLeft + delta);
    },
    [hasOverflow, scrollLeft, setClampedScrollLeft]
  );

  const setViewportWidthHandler = useCallback((width: number) => {
    setViewportWidth(width);
  }, []);

  const setScrollLeftHandler = useCallback((value: number) => {
    setScrollLeft(value);
  }, []);

  useEmployeesViewportEffects({
    viewportNode,
    maxScrollLeft,
    scrollLeft,
    setViewportWidth: setViewportWidthHandler,
    setScrollLeft: setScrollLeftHandler,
  });

  const trackTransform = useMemo(() => {
    if (!hasOverflow || scrollLeft <= 0) {
      return undefined;
    }

    return `translate3d(-${scrollLeft}px, 0, 0)`;
  }, [hasOverflow, scrollLeft]);

  return {
    viewportRef,
    visibleColumns,
    columnCount,
    columnWidth,
    totalTrackWidth,
    hasOverflow,
    isAtStart,
    isAtEnd,
    scrollLeft,
    scrollModel,
    scrollToPrevPageHandler,
    scrollToNextPageHandler,
    viewportNode,
    trackTransform,
    wheelHandler,
    dragState,
    dragStateChangeHandler,
  };
};
